<template>
  <div class="onBoading">
    <div class="onBoading__top">
      <img
        src="@/assets/logo-monespace.png"
        alt="Onboarding"
      />
      <div class="onBoading__top__content">
        <BasicText
          size="h2"
          color="white"
          weight="bold"
        >
          👋🏻 Bienvenue sur Mon Espace
        </BasicText>
        <BasicText
          size="h5"
          color="white"
          weight="light"
        >
          Accédez à votre portail AKTO pour gérer toutes vos démarches administratives liées à la formation et aux
          contrats en alternance. Vous pouvez déposer vos demandes de financement, suivre l'avancement de vos demandes
          et consulter vos documents utiles, le tout dans un espace sécurisé et optimisé.
        </BasicText>
        <div class="onBoading__top__content--button">
          <BasicButton
            label="Créer son compte"
            variant="outlined"
            size="large"
            @click="appState.inscriptionAsync"
          />
        </div>
      </div>
    </div>
    <div class="onBoading__middle">
      <div class="onBoading__middle__information">
        <div class="onBoading__middle__information__header">
          <BasicIcon
            name="info-circle"
            active
            color="primary-600"
          />
          <BasicText
            size="h5"
            color="secondary-800"
          >
            Informations
          </BasicText>
        </div>
        <BasicAlert alertType="info">
          <template #label>
            <BasicText color="secondary-800">
              Pensez à verser votre contribution conventionnelle à la formation et/ou au dialogue social le 28 février 2026 au plus tard.
              <br />
              Pour en savoir plus sur la contribution conventionnelle,
              <BasicLink
                label="cliquez ici"
                @link-click="redirectExterne('https://www.akto.fr/versement-contribution-conventionnelle-28-fevrier-2026/')"
              />.
              Pour accéder au portail en ligne de versement :
              <BasicLink
                label="declaration.akto.fr"
                @link-click="redirectExterne('https://declaration.akto.fr')"
              />
            </BasicText>
          </template>
        </BasicAlert>

        <BasicAlert alertType="warning">
          <template #label>
            <BasicText color="secondary-800">
              Bienvenue aux entreprises de travail temporaire qui rejoignent Mon Espace.
              <br/><br/>
              Découvrez votre mode de connexion en fonction de votre situation :
              <br />
              <ul>
                <li><BasicText color="secondary-800">Vous avez un compte sur Mes Démarches Administratives (MDA) et vous avez déjà un compte sur Mon Espace :<br/>Rien ne change, connectez-vous à votre compte Mon Espace existant.</BasicText></li>
                <li><BasicText color="secondary-800">Vous avez un compte sur MDA, mais vous n’avez pas encore de compte sur Mon Espace :<br/>Créez votre compte sur Mon Espace en utilisant la même adresse email que celle de votre compte Mes Démarches Administratives et choisissez un nouveau mot de passe.</BasicText></li>
                <li><BasicText color="secondary-800">Vous n’avez pas de compte ni sur MDA, ni sur Mon Espace :<br/>Créez votre compte sur Mon Espace et faites une demande de rattachement à votre entreprise en suivant le guide de <BasicLink
                label="connexion / création de compte"
                @link-click="redirectExterne('https://s.akto.fr/EJCF4N')"
              />.</BasicText></li>
              </ul>
            </BasicText>
          </template>
        </BasicAlert>


        <BasicText
          color="secondary-800"
          weight="bold"
        >
          Accédez à Mon Espace 
        </BasicText>

        <BasicText color="secondary-800">
          Si vous n'avez pas encore de compte, vous pouvez le créer à l'aide de notre <BasicLink
              label="guide détaillé"
              @link-click="redirectExterne('https://s.akto.fr/EJCF4N')"
            />
        </BasicText>
        <BasicText
          color="secondary-800"
          weight="bold"
        >
          Vous avez besoin d'aide pour accéder à Mon Espace ?
        </BasicText>
        <BasicText color="secondary-800">
          Nos équipes sont à votre disposition pour vous accompagner à chaque étape. N'hésitez pas à nous contacter via
          notre
          <BasicLink
            label="formulaire de contact"
            @link-click="
              redirectExterne(
                'https://www.akto.fr/nous-contacter/entreprises/#vous-n-avez-pas-trouve-votre-reponse-n-hesitez-pas-a-nous-contacter',
              )
            "
          />
        </BasicText>
      </div>
      <div class="onBoading__middle__actualite">
        <cards-layout
          cards-type="Actualités"
          headerText="Agenda"
          :cards="cardsListAgendas"
          :redirection="() => redirectExterne('https://www.akto.fr/agenda/')"
        />
      </div>
    </div>
    <div class="onBoading__bottom">
      <div class="onBoading__bottom__header">
        <BasicText
          size="h5"
          color="secondary-800"
        >
          Articles
        </BasicText>
        <BasicLink
          label="Tout voir"
          @link-click="redirectExterne('https://www.akto.fr/articles/')"
        />
      </div>
      <div class="onBoading__bottom__articles">
        <div
          class="onBoading__bottom__articles--cards"
          v-for="actu in actualites"
          :key="actu.url!"
        >
          <BasicText
            color="secondary-800"
            weight="bold"
          >
            {{ dateFormatter(actu.date!) }}
          </BasicText>
          <BasicText
            color="secondary-800"
            weight="bold"
            size="body-xl"
          >
            <BasicLink
              :label="actu.title!"
              @link-click="redirectExterne(actu.url!)"
            />
          </BasicText>
          <BasicText
            color="dark-jungle-green"
            size="body-m"
          >
            {{ truncateEllipsis(actu.text!, 200) }}
          </BasicText>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useAppStateStore } from '@/features/application'
import CardsLayout from '@/features/interface/home/tableauDeBord/cardsLayout/CardsLayout.vue'
import { dateFormatter, truncateEllipsis } from '@/features/shared/tools'
import { redirectExterne } from '@/features/shared/tools/url'
import { storeToRefs } from 'pinia'
import { useOnboardingStore } from './useOnboardingStore'

  const appState = useAppStateStore()
  const onboardingStore = useOnboardingStore()
  const { actualites, cardsListAgendas } = storeToRefs(onboardingStore)
</script>

<style scoped lang="less">
  @import './OnboardingView.less';
</style>
